import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DragHandleIcon from "@mui/icons-material/DragIndicator";
import {
    Autocomplete,
    Grid,
    TextField,
    Typography,
    createFilterOptions,
    styled,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { ArrayHelpers, FieldArray, Form, Formik, FormikValues } from "formik";
import * as yup from "yup";
import {
    BANNER_PLACEHOLDER_MESSAGE_WEB,
    BANNER_ERROR_MESSAGE,
    BANNER_ACCEPTED_FILE,
    NO_CONTENT_PLAYLIST,
    IMAGE_URL,
    COLLECTION_DIMENSION,
    BANNER_PLACEHOLDER_MESSAGE_MOBILE,
    MOBILE_IMAGE_URL,
} from "../LandingPage/constants";
import { DeleteForeverRounded } from "@mui/icons-material";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { StyledDropzone } from "../../UI/Common/FileUploader";
import axios from "../../../utils/axiosInstance";
import { enqueueSnackbar, useSnackbar } from "notistack";
import { getPlaylistCount } from "../../../utilities/helpers/helperService";
import GeneralModalV1 from "../../UI/atoms/Popup/GeneralModalV1";

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
}));

export const dynamicValidationSchema = yup.object().shape({
    collections: yup
        .array()
        .of(
            yup.object().shape({
                titleText: yup.string().required("Title text is required"),
                playlistId: yup.string().required("Playlist is required"),
                imageUrl: yup.string().required("Image is required"),
                mobileImageUrl: yup
                    .string()
                    .required("Mobile image is required"),
                playlistCount: yup
                    .number()
                    .when("playlistId", ([playlistId], schema) => {
                        return playlistId
                            ? schema.required()
                            : schema.notRequired();
                    }),
            })
        )
        .min(1)
        .required(),
});

const CollectionForm = forwardRef(
    (
        { handleCollections, collections, draft, allowEdit, playlist }: any,
        ref: any
    ) => {
        const [isOpen, setIsOpen] = React.useState<any>(null);
        const formikRef = useRef<any>(null);
        const [initialValue, setInitialValue] = useState<any>([]);
        const [formSubmitInitated, setFormSubmitInitiated] = useState(false);
        const [formChange, setFormChange] = useState<any>(null);
        const [imageLoader, setImageLoader] = useState<any[]>([]);
        const [mobileImageLoader, setmobileImageLoader] = useState<any[]>([]);

        const [imageErrMessage, setImageErrMessage] = useState<any[]>([]);
        const [mobileImageErrMessage, setmobileImageErrMessage] = useState<
            any[]
        >([]);
        const [showInfoPopup, setShowInfoPopup] = useState(false);
        const [selectedFile, setSelectedFile] = useState<any>(null);
        const [selectedIndex, setSelectedIndex] = useState<any>(null);
        const [playlistInfo, setPlaylistInfo] = useState<any>([]);

        const initialValuesState = {
            titleText: null,
            playlistId: null,
            playlistCount: null,
            imageUrl: null,
            mobileImageUrl: null,
        };

        useEffect(() => {
            setPlaylistInfo(playlist);
        }, [playlist]);

        // To handle new/draft/no data for collection rail
        useEffect(() => {
            if (draft) {
                setInitialValue(draft);
            } else if (collections) {
                setInitialValue(collections);
            } else {
                setInitialValue([]);
            }
        }, [collections]);

        // Autocomplete open & close
        const handleOpenOptions = (index: number) => {
            setIsOpen(index);
        };

        const handleCloseOptions = (index: number) => {
            setIsOpen(null);
        };

        // formik validation

        const handleOnSubmit = async (values: FormikValues) => {
            handleCollections(values);
        };

        const handleSubmitFormik = () => {
            console.log("formikRef.current", formikRef.current);
            formikRef.current.submitForm();
            setFormSubmitInitiated(false);
        };

        const getFormValues = () => {
            console.log("formikRef.current", formikRef.current);
            return formikRef.current.values;
        };

        const isValidForm = () => {
            console.log("formikRef.current", formikRef.current);
            const isValid = isValidCheck();
            console.log("isValid", isValid);
            if (!isValid) {
                setFormSubmitInitiated(true);
            }
            return isValid;
        };

        const isValidCheck = () => {
            try {
                dynamicValidationSchema.validateSync(formikRef.current.values);
                return true;
            } catch (error) {
                console.log(error);
                return false;
            }
        };

        const validateInput = (inputJson: any) => {
            const validation = dynamicValidationSchema.validate(inputJson);
            console.log("validation", validation);
        };

        useImperativeHandle(
            ref,
            () => ({
                handleSubmitFormik,
                getFormValues,
                isValidForm,
                validateInput,
                validateForm: () => formikRef.current.validateForm(),
            }),
            [formChange]
        );

        const grid = 4;

        const getItemStyle = (isDragging: any, draggableStyle: any) => ({
            // some basic styles to make the items look a bit nicer
            userSelect: "none",
            padding: grid * 2,
            margin: 0,
            marginTop: 10,
            backgound: "white !important",
            ...draggableStyle,
        });

        const getListStyle = (isDraggingOver: any) => ({
            padding: 10,
            width: "100%",
            overflow: "hidden",
        });

        const onDragEnd = (result: any) => {
            try {
                let currentValues: any[] = [];
                if (!result.destination) {
                    return;
                }
                if (
                    result?.destination?.droppableId ===
                        result?.source?.droppableId &&
                    result?.destination?.index === result?.source?.index
                ) {
                    return;
                }
                currentValues = [...formikRef?.current?.values?.collections];
                const reordered = reorder(
                    currentValues,
                    result?.source?.index,
                    result?.destination?.index
                );
                formikRef.current.setValues({ collections: reordered });
            } catch (error) {
                console.log("Error when drag end", error);
            }
        };

        const reorder = (list: any, startIndex: any, endIndex: any) => {
            const result = Array.from(list);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            return result;
        };

        const uploadImage = (files: any, index: number, fieldName: string) => {
            // check if the image is already -present
            const collections = formikRef.current.values?.collections;
            if (collections && collections[index][fieldName]) {
                // open dialog for confirmation
                setShowInfoPopup(true);
                setFileAndIndex(files, index, fieldName);
            } else {
                validateAndUploadFile(files[0], index, fieldName);
            }
        };

        const closeInfoModal = () => {
            setShowInfoPopup(false);
            revertFileIndex();
        };

        // upload confirmation
        const confirmUpload = () => {
            validateAndUploadFile(
                selectedFile?.files[0],
                selectedIndex,
                selectedFile?.fieldName
            );
            revertFileIndex();
            setShowInfoPopup(false);
        };

        const revertFileIndex = () => {
            setSelectedFile(null);
            setSelectedIndex(null);
        };

        const setFileAndIndex = (
            files: any,
            index: number,
            fieldName: string
        ) => {
            setSelectedFile({ files, fieldName });
            setSelectedIndex(index);
        };

        const isAspectRatio2to3 = (width: number, height: number): boolean => {
            const aspectRatio = width / height;
            const targetRatio = 2 / 3;
            const tolerance = 0.01;
            return Math.abs(aspectRatio - targetRatio) < tolerance;
        };

        // Validate the file dimension & proceed with uploading the file
        const validateAndUploadFile = (
            file: any,
            index: number,
            fieldName: string
        ) => {
            let minHeight: any,
                minWidth: any,
                setImgErrMessage: any,
                imgErrMessage: any;
            if (fieldName == MOBILE_IMAGE_URL) {
                minHeight = COLLECTION_DIMENSION.MOBILE.HEIGHT;
                minWidth = COLLECTION_DIMENSION.MOBILE.WIDTH;
                setImgErrMessage = setmobileImageErrMessage;
                imgErrMessage = mobileImageErrMessage;
            } else {
                minHeight = COLLECTION_DIMENSION.WEB.HEIGHT;
                minWidth = COLLECTION_DIMENSION.WEB.WIDTH;
                setImgErrMessage = setImageErrMessage;
                imgErrMessage = imageErrMessage;
            }
            // CHECK ACCEPTED FILE FORMATS
            if (!(file && BANNER_ACCEPTED_FILE.includes(file.type))) return;
            const image = new Image();
            image.addEventListener("load", () => {
                console.log(`${image.width}x${image.height}`);
                if (
                    (fieldName === IMAGE_URL &&
                        image.width >= minWidth &&
                        image.height >= minHeight) ||
                    (fieldName === MOBILE_IMAGE_URL &&
                        image.width >= minWidth &&
                        image.height >= minHeight &&
                        isAspectRatio2to3(image.width, image.height))
                ) {
                    uploadImageAPI(file, index, fieldName);
                    //incase if the image container already has error message shown, remove it since dimension is good for upload
                    if (imgErrMessage.includes(index)) {
                        const updatedImageErrIndex = imgErrMessage.filter(
                            (number: any) => number !== index
                        );
                        setImgErrMessage(updatedImageErrIndex);
                    }
                } else {
                    // if the dimension is not good enough. Add the index of the banner to the error
                    if (!imgErrMessage.includes(index)) {
                        const errIndices = [...imgErrMessage, index];
                        setImgErrMessage(errIndices);
                    }
                }
            });
            image.src = URL.createObjectURL(file);
        };

        // API for uploading image
        const uploadImageAPI = async (
            file: any,
            index: number,
            fieldName: string
        ) => {
            try {
                if (file) {
                    const formData = new FormData();
                    formData.append("file", file);
                    addToLoader(index, fieldName);
                    const uploadFile: any = await axios.post(
                        `${process.env.BASE_URL}api/v1/cms/page/upload-banner`,
                        formData
                    );
                    if (uploadFile?.data?.success) {
                        // if upload is success, update the image url of the specified banner index
                        const imageUrl = uploadFile?.data?.data?.url;
                        let collections = formikRef.current.values?.collections;
                        collections[index][fieldName] = imageUrl;
                        formikRef.current.setValues({ collections });
                        enqueueSnackbar("Collection Image Uploaded", {
                            variant: "info",
                            autoHideDuration: 2000,
                            anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "center",
                            },
                        });
                    }
                }
            } catch (error: any) {
                console.log("Error uploading", error.response.data.message);
                if (error?.response?.data?.message) {
                    enqueueSnackbar(error?.response?.data?.message, {
                        variant: "error",
                    });
                } else {
                    enqueueSnackbar("Error when uploading image", {
                        variant: "error",
                    });
                }
            } finally {
                removeFromLoader(index, fieldName);
            }
        };

        // Add to image loader
        // Add to image loader
        const addToLoader = (index: number, fieldName: string) => {
            let loader =
                fieldName == IMAGE_URL ? imageLoader : mobileImageLoader;
            let setLoader =
                fieldName == IMAGE_URL ? setImageLoader : setmobileImageLoader;
            if (!loader.includes(index)) {
                setLoader([...loader, index]);
            }
        };

        // remove from image loader
        const removeFromLoader = (index: number, fieldName: string) => {
            let loader =
                fieldName == IMAGE_URL ? imageLoader : mobileImageLoader;
            let setLoader =
                fieldName == IMAGE_URL ? setImageLoader : setmobileImageLoader;
            const updatedImageLoaderIndex = loader.filter(
                (number) => number !== index
            );
            setLoader(updatedImageLoaderIndex);
        };

        const renderOptionsInitialPlaylist = (options: any) => {
            if (typeof options == "string") {
                const filterPlaylist = playlistInfo.filter(
                    (list: any) => list.id === options
                );
                if (filterPlaylist.length > 0) {
                    return (
                        filterPlaylist[0].name + " - " + filterPlaylist[0].id
                    );
                } else {
                    return "";
                }
            } else if (typeof options == "object") {
                return options.name + " - " + options.id;
            }
        };

        const filterOptions = createFilterOptions({
            matchFrom: "any",
            stringify: (option: any) => option.name + option.id,
        });

        return (
            <div className="d-flex float-center flex-column m-2">
                <Formik
                    innerRef={formikRef}
                    initialValues={{
                        collections: initialValue,
                    }}
                    enableReinitialize
                    validationSchema={dynamicValidationSchema}
                    onSubmit={handleOnSubmit}
                >
                    {({
                        values,
                        handleChange,
                        touched,
                        errors,
                        setFieldValue,
                        setFieldTouched,
                        handleBlur,
                    }: any) => (
                        <Form className="config-form">
                            <FieldArray
                                name="collections"
                                render={(arrayHelpers: ArrayHelpers) => {
                                    const collections = values.collections;
                                    return (
                                        <div>
                                            {collections &&
                                                collections.length == 0 && (
                                                    <Grid>
                                                        <Grid item xs={12}>
                                                            <div className="d-flex flex-column j-center align-item-center">
                                                                <AddCircleOutlineIcon
                                                                    onClick={() => {
                                                                        arrayHelpers.push(
                                                                            {
                                                                                ...initialValuesState,
                                                                            }
                                                                        );
                                                                    }}
                                                                    className="cursor-pointer"
                                                                />
                                                                <span>
                                                                    Add
                                                                    Collection
                                                                </span>
                                                            </div>
                                                        </Grid>
                                                    </Grid>
                                                )}
                                            <DragDropContext
                                                onDragEnd={onDragEnd}
                                            >
                                                <Droppable
                                                    droppableId="droppable-child-curation"
                                                    type={`child`}
                                                    key={`droppable-collection-container`}
                                                >
                                                    {(provided, snapshot) => (
                                                        <div
                                                            className="d-flex j-center mt-1 flex-column"
                                                            {...provided.droppableProps}
                                                            ref={
                                                                provided.innerRef
                                                            }
                                                            style={getListStyle(
                                                                snapshot.isDraggingOver
                                                            )}
                                                        >
                                                            {collections &&
                                                                collections.length >
                                                                    0 &&
                                                                collections.map(
                                                                    (
                                                                        item: any,
                                                                        index: number
                                                                    ) => (
                                                                        <Draggable
                                                                            isDragDisabled={
                                                                                !allowEdit
                                                                            }
                                                                            key={
                                                                                index
                                                                            }
                                                                            draggableId={`collections-${index}`}
                                                                            index={
                                                                                index
                                                                            }
                                                                        >
                                                                            {(
                                                                                provided,
                                                                                snapshot
                                                                            ) => (
                                                                                <Grid
                                                                                    ref={
                                                                                        provided.innerRef
                                                                                    }
                                                                                    {...provided.draggableProps}
                                                                                    {...provided.dragHandleProps}
                                                                                    style={getItemStyle(
                                                                                        snapshot.isDragging,
                                                                                        provided
                                                                                            .draggableProps
                                                                                            .style
                                                                                    )}
                                                                                    key={
                                                                                        index
                                                                                    }
                                                                                    className="banner-card box-shadow-s mb-3 bg-white pd-2 w-auto"
                                                                                    container
                                                                                    spacing={
                                                                                        2
                                                                                    }
                                                                                >
                                                                                    <Grid
                                                                                        className="d-flex align-item-center"
                                                                                        item
                                                                                    >
                                                                                        <DragHandleIcon className="drag-handle" />
                                                                                    </Grid>
                                                                                    <Grid
                                                                                        item
                                                                                        xs={
                                                                                            10
                                                                                        }
                                                                                        container
                                                                                        display={
                                                                                            "block"
                                                                                        }
                                                                                        spacing={
                                                                                            2
                                                                                        }
                                                                                    >
                                                                                        <Grid
                                                                                            item
                                                                                            xs={
                                                                                                12
                                                                                            }
                                                                                        >
                                                                                            <TextField
                                                                                                name={`collections.${index}.titleText`}
                                                                                                label="Title text"
                                                                                                fullWidth
                                                                                                value={
                                                                                                    values[
                                                                                                        `collections`
                                                                                                    ][
                                                                                                        index
                                                                                                    ][
                                                                                                        `titleText`
                                                                                                    ]
                                                                                                }
                                                                                                onChange={
                                                                                                    handleChange
                                                                                                }
                                                                                                size={
                                                                                                    "small"
                                                                                                }
                                                                                                InputLabelProps={{
                                                                                                    shrink: true,
                                                                                                }}
                                                                                                onBlur={
                                                                                                    handleBlur
                                                                                                }
                                                                                                disabled={
                                                                                                    !allowEdit
                                                                                                }
                                                                                                error={
                                                                                                    (touched[
                                                                                                        `collections`
                                                                                                    ]?.[
                                                                                                        index
                                                                                                    ]
                                                                                                        ?.titleText ||
                                                                                                        formSubmitInitated) &&
                                                                                                    errors[
                                                                                                        `collections`
                                                                                                    ]?.[
                                                                                                        index
                                                                                                    ]
                                                                                                        ?.titleText
                                                                                                }
                                                                                                helperText={
                                                                                                    (touched[
                                                                                                        `collections`
                                                                                                    ]?.[
                                                                                                        index
                                                                                                    ]
                                                                                                        ?.titleText ||
                                                                                                        formSubmitInitated) &&
                                                                                                    errors[
                                                                                                        `collections`
                                                                                                    ]?.[
                                                                                                        index
                                                                                                    ]
                                                                                                        ?.titleText
                                                                                                }
                                                                                            />
                                                                                        </Grid>
                                                                                        <Grid
                                                                                            item
                                                                                            xs={
                                                                                                12
                                                                                            }
                                                                                        >
                                                                                            <Autocomplete
                                                                                                id={
                                                                                                    `bcPlaylistId` +
                                                                                                    index
                                                                                                }
                                                                                                options={
                                                                                                    playlistInfo
                                                                                                }
                                                                                                filterOptions={
                                                                                                    filterOptions
                                                                                                }
                                                                                                value={
                                                                                                    values[
                                                                                                        `collections`
                                                                                                    ][
                                                                                                        index
                                                                                                    ][
                                                                                                        `playlistId`
                                                                                                    ]
                                                                                                }
                                                                                                includeInputInList
                                                                                                open={
                                                                                                    isOpen ==
                                                                                                    index
                                                                                                }
                                                                                                size={
                                                                                                    "small"
                                                                                                }
                                                                                                fullWidth
                                                                                                onChange={async (
                                                                                                    e,
                                                                                                    value
                                                                                                ) => {
                                                                                                    setFieldTouched(
                                                                                                        `collections.${index}.playlistId`,
                                                                                                        true
                                                                                                    );
                                                                                                    const playlistCount =
                                                                                                        await getPlaylistCount(
                                                                                                            value?.id
                                                                                                        );
                                                                                                    await setFieldValue(
                                                                                                        `collections.${index}.playlistId`,
                                                                                                        value?.id ||
                                                                                                            ""
                                                                                                    );
                                                                                                    await setFieldValue(
                                                                                                        `collections.${index}.playlistCount`,
                                                                                                        playlistCount
                                                                                                    );
                                                                                                }}
                                                                                                onOpen={() =>
                                                                                                    handleOpenOptions(
                                                                                                        index
                                                                                                    )
                                                                                                }
                                                                                                onClose={() =>
                                                                                                    handleCloseOptions(
                                                                                                        index
                                                                                                    )
                                                                                                }
                                                                                                getOptionLabel={(
                                                                                                    options: any
                                                                                                ) => {
                                                                                                    return (
                                                                                                        renderOptionsInitialPlaylist(
                                                                                                            options
                                                                                                        ) ||
                                                                                                        ""
                                                                                                    );
                                                                                                }}
                                                                                                disabled={
                                                                                                    !allowEdit
                                                                                                }
                                                                                                renderInput={(
                                                                                                    params
                                                                                                ) => (
                                                                                                    <TextField
                                                                                                        name={`collections.${index}.playlistId`}
                                                                                                        label="BC Playlist Id"
                                                                                                        value={
                                                                                                            values.playlistId
                                                                                                        }
                                                                                                        error={
                                                                                                            (touched[
                                                                                                                `collections`
                                                                                                            ]?.[
                                                                                                                index
                                                                                                            ]
                                                                                                                ?.playlistId ||
                                                                                                                formSubmitInitated) &&
                                                                                                            (Boolean(
                                                                                                                errors[
                                                                                                                    `collections`
                                                                                                                ]?.[
                                                                                                                    index
                                                                                                                ]
                                                                                                                    ?.playlistId
                                                                                                            ) ||
                                                                                                                Boolean(
                                                                                                                    errors[
                                                                                                                        `collections`
                                                                                                                    ]?.[
                                                                                                                        index
                                                                                                                    ]
                                                                                                                        ?.playlistCount
                                                                                                                ))
                                                                                                        }
                                                                                                        // helperText={((touched[`collections`]?.[index]?.playlistId || formSubmitInitated) &&
                                                                                                        //  errors[`collections`]?.[index]?.playlistId
                                                                                                        //  || values[`collections`][index][`playlistId`])
                                                                                                        //     ? (!values[`collections`][index][`playlistCount`] ? NO_CONTENT_PLAYLIST : errors[`collections`]?.[index]?.playlistId) :
                                                                                                        //     ''}

                                                                                                        helperText={
                                                                                                            (touched[
                                                                                                                `collections`
                                                                                                            ]?.[
                                                                                                                index
                                                                                                            ]
                                                                                                                ?.playlistId ||
                                                                                                                formSubmitInitated) &&
                                                                                                            errors[
                                                                                                                `collections`
                                                                                                            ]?.[
                                                                                                                index
                                                                                                            ]
                                                                                                                ?.playlistId
                                                                                                                ? errors[
                                                                                                                      `collections`
                                                                                                                  ]?.[
                                                                                                                      index
                                                                                                                  ]
                                                                                                                      ?.playlistId
                                                                                                                : values[
                                                                                                                      `collections`
                                                                                                                  ][
                                                                                                                      index
                                                                                                                  ][
                                                                                                                      `playlistId`
                                                                                                                  ]
                                                                                                                ? !values[
                                                                                                                      `collections`
                                                                                                                  ][
                                                                                                                      index
                                                                                                                  ][
                                                                                                                      `playlistCount`
                                                                                                                  ]
                                                                                                                    ? NO_CONTENT_PLAYLIST
                                                                                                                    : ""
                                                                                                                : ""
                                                                                                        }
                                                                                                        {...params}
                                                                                                    />
                                                                                                )}
                                                                                            />
                                                                                        </Grid>
                                                                                    </Grid>
                                                                                    <Grid
                                                                                        item
                                                                                        container
                                                                                        xs={
                                                                                            12
                                                                                        }
                                                                                        spacing={
                                                                                            2
                                                                                        }
                                                                                        marginLeft={
                                                                                            4
                                                                                        }
                                                                                    >
                                                                                        <Grid
                                                                                            item
                                                                                            xs={
                                                                                                5
                                                                                            }
                                                                                        >
                                                                                            <Typography
                                                                                                variant="subtitle1"
                                                                                                gutterBottom
                                                                                            >
                                                                                                CTV/Web/Tablet
                                                                                            </Typography>
                                                                                            <Item>
                                                                                                <StyledDropzone
                                                                                                    height={
                                                                                                        100
                                                                                                    }
                                                                                                    loaderHeight={
                                                                                                        100
                                                                                                    }
                                                                                                    disabled={
                                                                                                        !allowEdit
                                                                                                    }
                                                                                                    onDropFile={(
                                                                                                        file: any
                                                                                                    ) => {
                                                                                                        uploadImage(
                                                                                                            file,
                                                                                                            index,
                                                                                                            "imageUrl"
                                                                                                        );
                                                                                                        setFieldTouched(
                                                                                                            `collections.${index}.imageUrl`,
                                                                                                            true
                                                                                                        );
                                                                                                    }}
                                                                                                    multiple={
                                                                                                        false
                                                                                                    }
                                                                                                    onRemoveImage={() => {
                                                                                                        setFieldValue(
                                                                                                            `collections.${index}.imageUrl`,
                                                                                                            null
                                                                                                        );
                                                                                                        setFieldTouched(
                                                                                                            `collections.${index}.imageUrl`,
                                                                                                            true
                                                                                                        );
                                                                                                    }}
                                                                                                    imageUrl={
                                                                                                        values[
                                                                                                            `collections`
                                                                                                        ][
                                                                                                            index
                                                                                                        ][
                                                                                                            `imageUrl`
                                                                                                        ]
                                                                                                    }
                                                                                                    errorMessage={
                                                                                                        imageErrMessage.includes(
                                                                                                            index
                                                                                                        )
                                                                                                            ? BANNER_ERROR_MESSAGE
                                                                                                            : null
                                                                                                    }
                                                                                                    errorOutline={
                                                                                                        ((touched[
                                                                                                            `collections`
                                                                                                        ] &&
                                                                                                            touched[
                                                                                                                `collections`
                                                                                                            ][
                                                                                                                index
                                                                                                            ]
                                                                                                                ?.imageUrl) ||
                                                                                                            formSubmitInitated) &&
                                                                                                        errors[
                                                                                                            `collections`
                                                                                                        ] &&
                                                                                                        errors[
                                                                                                            `collections`
                                                                                                        ][
                                                                                                            index
                                                                                                        ]
                                                                                                            ?.imageUrl
                                                                                                    }
                                                                                                    loader={imageLoader.includes(
                                                                                                        index
                                                                                                    )}
                                                                                                    placeholderMessage={
                                                                                                        Boolean(
                                                                                                            ((touched[
                                                                                                                `collections`
                                                                                                            ] &&
                                                                                                                touched[
                                                                                                                    `collections`
                                                                                                                ][
                                                                                                                    index
                                                                                                                ]
                                                                                                                    ?.imageUrl) ||
                                                                                                                formSubmitInitated) &&
                                                                                                                errors[
                                                                                                                    `collections`
                                                                                                                ] &&
                                                                                                                errors[
                                                                                                                    `collections`
                                                                                                                ][
                                                                                                                    index
                                                                                                                ]
                                                                                                                    ?.imageUrl
                                                                                                        )
                                                                                                            ? errors[
                                                                                                                  `collections`
                                                                                                              ][
                                                                                                                  index
                                                                                                              ]
                                                                                                                  ?.imageUrl
                                                                                                            : BANNER_PLACEHOLDER_MESSAGE_WEB
                                                                                                    }
                                                                                                />
                                                                                            </Item>
                                                                                        </Grid>
                                                                                        <Grid
                                                                                            item
                                                                                            xs={
                                                                                                5
                                                                                            }
                                                                                        >
                                                                                            <Typography
                                                                                                variant="subtitle1"
                                                                                                gutterBottom
                                                                                            >
                                                                                                Mobile
                                                                                            </Typography>
                                                                                            <Item>
                                                                                                <StyledDropzone
                                                                                                    height={
                                                                                                        100
                                                                                                    }
                                                                                                    loaderHeight={
                                                                                                        100
                                                                                                    }
                                                                                                    disabled={
                                                                                                        !allowEdit
                                                                                                    }
                                                                                                    onDropFile={(
                                                                                                        file: any
                                                                                                    ) => {
                                                                                                        uploadImage(
                                                                                                            file,
                                                                                                            index,
                                                                                                            MOBILE_IMAGE_URL
                                                                                                        );
                                                                                                        setFieldTouched(
                                                                                                            `collections.${index}.mobileImageUrl`,
                                                                                                            true
                                                                                                        );
                                                                                                    }}
                                                                                                    multiple={
                                                                                                        false
                                                                                                    }
                                                                                                    onRemoveImage={() => {
                                                                                                        setFieldValue(
                                                                                                            `collections.${index}.mobileImageUrl`,
                                                                                                            null
                                                                                                        );
                                                                                                        setFieldTouched(
                                                                                                            `collections.${index}.mobileImageUrl`,
                                                                                                            true
                                                                                                        );
                                                                                                    }}
                                                                                                    imageUrl={
                                                                                                        values[
                                                                                                            `collections`
                                                                                                        ][
                                                                                                            index
                                                                                                        ][
                                                                                                            `mobileImageUrl`
                                                                                                        ]
                                                                                                    }
                                                                                                    errorMessage={
                                                                                                        mobileImageErrMessage.includes(
                                                                                                            index
                                                                                                        )
                                                                                                            ? BANNER_ERROR_MESSAGE
                                                                                                            : null
                                                                                                    }
                                                                                                    errorOutline={
                                                                                                        ((touched[
                                                                                                            `collections`
                                                                                                        ] &&
                                                                                                            touched[
                                                                                                                `collections`
                                                                                                            ][
                                                                                                                index
                                                                                                            ]
                                                                                                                ?.mobileImageUrl) ||
                                                                                                            formSubmitInitated) &&
                                                                                                        errors[
                                                                                                            `collections`
                                                                                                        ] &&
                                                                                                        errors[
                                                                                                            `collections`
                                                                                                        ][
                                                                                                            index
                                                                                                        ]
                                                                                                            ?.mobileImageUrl
                                                                                                    }
                                                                                                    loader={mobileImageLoader.includes(
                                                                                                        index
                                                                                                    )}
                                                                                                    placeholderMessage={
                                                                                                        Boolean(
                                                                                                            ((touched[
                                                                                                                `collections`
                                                                                                            ] &&
                                                                                                                touched[
                                                                                                                    `collections`
                                                                                                                ][
                                                                                                                    index
                                                                                                                ]
                                                                                                                    ?.mobileImageUrl) ||
                                                                                                                formSubmitInitated) &&
                                                                                                                errors[
                                                                                                                    `collections`
                                                                                                                ] &&
                                                                                                                errors[
                                                                                                                    `collections`
                                                                                                                ][
                                                                                                                    index
                                                                                                                ]
                                                                                                                    ?.mobileImageUrl
                                                                                                        )
                                                                                                            ? errors[
                                                                                                                  `collections`
                                                                                                              ][
                                                                                                                  index
                                                                                                              ]
                                                                                                                  ?.mobileImageUrl
                                                                                                            : BANNER_PLACEHOLDER_MESSAGE_MOBILE
                                                                                                    }
                                                                                                />
                                                                                            </Item>
                                                                                        </Grid>
                                                                                    </Grid>
                                                                                    <span className="delete-circle-style">
                                                                                        {allowEdit && (
                                                                                            <DeleteForeverRounded
                                                                                                className="cursor-pointer"
                                                                                                onClick={() => {
                                                                                                    arrayHelpers.remove(
                                                                                                        index
                                                                                                    );
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                    </span>
                                                                                </Grid>
                                                                            )}
                                                                        </Draggable>
                                                                    )
                                                                )}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </DragDropContext>

                                            {collections &&
                                                collections.length > 0 &&
                                                allowEdit && (
                                                    <Grid
                                                        item
                                                        xs={12}
                                                        className="mt-1"
                                                    >
                                                        <div className="d-flex flex-column j-center align-item-center">
                                                            <AddCircleOutlineIcon
                                                                onClick={() => {
                                                                    arrayHelpers.push(
                                                                        {
                                                                            ...initialValuesState,
                                                                        }
                                                                    );
                                                                }}
                                                                className="cursor-pointer"
                                                            />
                                                        </div>
                                                    </Grid>
                                                )}
                                        </div>
                                    );
                                }}
                            />
                        </Form>
                    )}
                </Formik>
                <GeneralModalV1
                    title="Confirm upload"
                    isOpen={showInfoPopup}
                    handleClose={closeInfoModal}
                    handleConfirm={confirmUpload}
                >
                    <p>
                        {" "}
                        Replace current image with{" "}
                        {selectedFile && selectedFile?.files[0]?.name}?
                    </p>
                </GeneralModalV1>
            </div>
        );
    }
);

export default CollectionForm;
